import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getProjetosCompleto } from "../../api/projetos.api";
import { atualizarTarefa } from "../../api/tarefas.api";
import { setAlertaCount, setAlertaTarefas } from "../../utils/tarefasAlerta";
import { enviarPrazosAgora } from "../../api/configuracao.api";

/* ─── Categorias ─────────────────────────────────────────────────── */

const CATEGORIAS = [
  { key: "atrasada",  label: "Atrasadas",       cor: "#f87171", bg: "rgba(248,113,113,0.12)", icon: "⚠"  },
  { key: "hoje",      label: "Hoje / Amanhã",   cor: "#fb923c", bg: "rgba(251,146,60,0.12)",  icon: "⏰" },
  { key: "em7dias",   label: "Próximos 7 dias", cor: "#fbbf24", bg: "rgba(251,191,36,0.12)",  icon: "📅" },
  { key: "sem_prazo", label: "Sem prazo",        cor: "#64748b", bg: "rgba(100,116,139,0.12)", icon: "○"  },
];

const PROXIMAS_ACOES = {
  "Nao Iniciada": [{ label: "Iniciar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Em Andamento": [
    { label: "Concluir", key: "Concluida", cor: "#4ade80" },
    { label: "Bloquear", key: "Bloqueada", cor: "#f87171" },
  ],
  "Bloqueada":    [{ label: "Retomar",  key: "Em Andamento", cor: "#60a5fa" }],
  "Concluida":    [],
};

const STATUS_COR = {
  "Nao Iniciada": "#64748b",
  "Em Andamento": "#60a5fa",
  "Bloqueada":    "#f87171",
  "Concluida":    "#4ade80",
};

const STATUS_LABEL = {
  "Nao Iniciada": "Não Iniciada",
  "Em Andamento": "Em Andamento",
  "Bloqueada":    "Bloqueada",
  "Concluida":    "Concluída",
};

function getCategoria(tarefa) {
  if (tarefa.status === "Concluida") return null;
  if (!tarefa.data_entrega) return "sem_prazo";
  const hoje = new Date(new Date().toDateString());
  const prazo = new Date(tarefa.data_entrega);
  const diff = Math.floor((prazo - hoje) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return "atrasada";
  if (diff <= 1) return "hoje";
  if (diff <= 7) return "em7dias";
  return null;
}

function getDiffLabel(data_entrega) {
  if (!data_entrega) return null;
  const hoje  = new Date(new Date().toDateString());
  const prazo = new Date(data_entrega);
  const diff  = Math.floor((prazo - hoje) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return `${Math.abs(diff)} dia${Math.abs(diff) !== 1 ? "s" : ""} de atraso`;
  if (diff === 0) return "vence hoje";
  if (diff === 1) return "vence amanhã";
  return `${diff} dias restantes`;
}

function fmtDate(str) {
  if (!str) return "—";
  const [y, m, d] = str.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/* ─── Página ─────────────────────────────────────────────────────── */

export default function TarefasPrazos() {
  const [tarefas,    setTarefas]    = useState([]);
  const [ticketMap,  setTicketMap]  = useState({});
  const navigate = useNavigate();
  const [loading,    setLoading]    = useState(true);
  const [sending,    setSending]    = useState(false);
  const [filtro,     setFiltro]     = useState(() => {
    try { return localStorage.getItem("tarefas_prazos_filtro") || null; } catch { return null; }
  });
  const [filtroResp, setFiltroResp] = useState(() => {
    try { return localStorage.getItem("tarefas_prazos_resp") || ""; } catch { return ""; }
  });
  const [countdown,  setCountdown]  = useState(60);
  const cntRef   = useRef(60);
  const isFirst  = useRef(true);

  const load = useCallback(async (bust = false) => {
    try {
      const { projetos: rows, tarefas: allTarefas } = await getProjetosCompleto(bust, 2);

      // Mapa de todos os projetos para lookup do nome do pai
      const projetoMap = {};
      (rows || []).forEach(r => {
        if (r.id_projeto && !projetoMap[r.id_projeto]) {
          projetoMap[r.id_projeto] = { id: r.id_projeto, descricao: r.descricao, id_projeto_pai: r.id_projeto_pai };
        }
      });

      const tMap = {};
      (rows || []).forEach(r => {
        if (r.ticketid && !tMap[r.ticketid]) {
          const pai = r.id_projeto_pai ? projetoMap[r.id_projeto_pai] : null;
          tMap[r.ticketid] = {
            id:                    r.ticketid,
            subject:               r.subject || `#${r.ticketid}`,
            descricao_customizada: r.descricao_customizada || null,
            projeto:               r.descricao,
            projeto_pai:           pai?.descricao || null,
            desenvolvedor:         r.desenvolvedor,
            id_projeto:            r.id_projeto,
            id_projeto_pai:        r.id_projeto_pai,
          };
        }
      });
      setTicketMap(tMap);

      const all = allTarefas || [];
      setTarefas(all);
      const urgentes = all.filter(t => {
        const cat = getCategoria(t);
        return cat === "atrasada" || cat === "hoje";
      }).length;
      setAlertaCount(urgentes);
      setAlertaTarefas(
        all.filter(t => { const c = getCategoria(t); return c === "atrasada" || c === "hoje"; })
          .slice(0, 20).map(t => ({ ...t, _ticket: tMap[t.id_ticket] || null }))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      isFirst.current = false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (filtro) localStorage.setItem("tarefas_prazos_filtro", filtro);
    else localStorage.removeItem("tarefas_prazos_filtro");
  }, [filtro]);

  useEffect(() => {
    if (filtroResp) localStorage.setItem("tarefas_prazos_resp", filtroResp);
    else localStorage.removeItem("tarefas_prazos_resp");
  }, [filtroResp]);

  useEffect(() => {
    const t = setInterval(() => {
      cntRef.current -= 1;
      setCountdown(cntRef.current);
      if (cntRef.current <= 0) { cntRef.current = 60; setCountdown(60); load(true); }
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  function refresh() { cntRef.current = 60; setCountdown(60); load(true); }

  async function handleStatusChange(tarefa, novoStatus) {
    try {
      await atualizarTarefa(tarefa.id_tarefa, { ...tarefa, status: novoStatus });
      setTarefas(prev => prev.map(t =>
        t.id_tarefa === tarefa.id_tarefa ? { ...t, status: novoStatus } : t
      ));
    } catch { alert("Erro ao atualizar status."); }
  }

  async function handleSetPrazo(tarefa, data) {
    try {
      await atualizarTarefa(tarefa.id_tarefa, { ...tarefa, data_entrega: data });
      setTarefas(prev => prev.map(t =>
        t.id_tarefa === tarefa.id_tarefa ? { ...t, data_entrega: data } : t
      ));
    } catch { alert("Erro ao definir prazo."); }
  }

  /* ── Agrupamento ── */
  const responsaveis = [...new Set(tarefas.map(t => t.responsavel).filter(Boolean))].sort();

  const grupos = CATEGORIAS.map(cat => ({
    ...cat,
    itens: tarefas
      .filter(t => getCategoria(t) === cat.key && (!filtroResp || t.responsavel === filtroResp))
      .sort((a, b) => {
        if (!a.data_entrega) return 1;
        if (!b.data_entrega) return -1;
        return new Date(a.data_entrega) - new Date(b.data_entrega);
      }),
  }));

  const gruposFiltrados = filtro
    ? grupos.filter(g => g.key === filtro)
    : grupos.filter(g => g.itens.length > 0);

  if (loading && isFirst.current) {
    return <div style={skeletonPage}><span style={{ color: "#475569" }}>Carregando...</span></div>;
  }

  return (
    <div style={page}>

      {/* ── Header ── */}
      <div style={headerRow}>
        <div>
          <h1 style={pageTitle}>Prazos de Tarefas</h1>
          <div style={pageSub}>Tarefas que precisam de atenção</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={configBtn}
            onClick={() => navigate("/parametros")}
            title="Configurações gerais do sistema"
          >⚙ Configurações</button>
          <button
            style={{ ...configBtn, borderColor:"rgba(59,130,246,0.3)", color:"#60a5fa" }}
            onClick={async () => {
              setSending(true);
              try { await enviarPrazosAgora(); localStorage.setItem("tg_last_send", String(Date.now())); }
              catch {}
              setSending(false);
            }}
            disabled={sending}
            title="Enviar relatório de prazos via Telegram agora"
          >{sending ? "Enviando..." : "📤 Enviar"}</button>
          <button style={refreshBtn} onClick={refresh}>↻ {countdown}s</button>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={kpiRow}>
        {grupos.map(g => (
          <div
            key={g.key}
            style={{ ...kpiCard(g.cor, filtro === g.key) }}
            onClick={() => setFiltro(filtro === g.key ? null : g.key)}
          >
            <div style={{ fontSize: 22 }}>{g.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: g.cor, lineHeight: 1 }}>
              {g.itens.length}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{g.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filtro responsável ── */}
      {responsaveis.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#475569", fontSize: 12, whiteSpace: "nowrap" }}>👤 Responsável:</span>
          <select style={filtroRespSelect} value={filtroResp} onChange={e => setFiltroResp(e.target.value)}>
            <option value="">Todos</option>
            {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {filtroResp && (
            <button
              style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.08)", color: "#f87171", fontSize: 11, cursor: "pointer" }}
              onClick={() => setFiltroResp("")}
            >✕ Limpar</button>
          )}
        </div>
      )}

      {/* ── Listas ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {gruposFiltrados.map(grupo => (
          <div key={grupo.key}>
            <div style={grupoHeader(grupo.cor)}>
              <span>{grupo.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{grupo.label}</span>
              <span style={grupoBadge(grupo.cor)}>{grupo.itens.length}</span>
            </div>
            <div style={lista}>
              {grupo.itens.length === 0 && (
                <div style={emptyRow}>Nenhuma tarefa nesta categoria</div>
              )}
              {grupo.itens.map(tarefa => (
                <TarefaRow
                  key={tarefa.id_tarefa}
                  tarefa={tarefa}
                  ticket={ticketMap[tarefa.id_ticket] || {}}
                  categoria={grupo}
                  onStatusChange={s => handleStatusChange(tarefa, s)}
                  onSetPrazo={d => handleSetPrazo(tarefa, d)}
                />
              ))}
            </div>
          </div>
        ))}

        {gruposFiltrados.length === 0 && (
          <div style={allOkBox}>
            <div style={{ fontSize: 32 }}>✅</div>
            <div style={{ color: "#4ade80", fontSize: 16, fontWeight: 700 }}>Tudo em dia!</div>
            <div style={{ color: "#475569", fontSize: 13 }}>Nenhuma tarefa atrasada ou vencendo.</div>
          </div>
        )}
      </div>

    </div>
  );
}

/* ─── TarefaRow ──────────────────────────────────────────────────── */

function TarefaRow({ tarefa, ticket, categoria, onStatusChange, onSetPrazo }) {
  const [definindoPrazo, setDefinindoPrazo] = useState(false);
  const [novoPrazo,      setNovoPrazo]      = useState("");
  const acoes  = PROXIMAS_ACOES[tarefa.status] || [];
  const diff   = getDiffLabel(tarefa.data_entrega);
  const desc   = ticket.descricao_customizada || ticket.subject || "";

  async function salvarPrazo() {
    if (!novoPrazo) { setDefinindoPrazo(false); return; }
    await onSetPrazo(novoPrazo);
    setDefinindoPrazo(false);
    setNovoPrazo("");
  }

  return (
    <div style={row}>

      {/* Coluna esquerda: urgência */}
      <div style={urgCol}>
        <span style={urgBadge(categoria.cor)}>
          {diff || categoria.icon}
        </span>
        {tarefa.data_entrega && (
          <span style={{ fontSize: 10, color: "#475569" }}>{fmtDate(tarefa.data_entrega)}</span>
        )}
      </div>

      {/* Coluna central: contexto + descrição */}
      <div style={infoCol}>
        <div style={contextLine}>
          {ticket.projeto_pai && (
            <>
              <span style={projetoPaiChip}>{ticket.projeto_pai}</span>
              <span style={{ color: "#334155", fontSize: 10 }}>›</span>
            </>
          )}
          {ticket.projeto && <span style={projetoChip}>{ticket.projeto}</span>}
          <span style={ticketChip}>#{tarefa.id_ticket}{desc ? ` · ${desc}` : ""}</span>
        </div>
        <div style={tarefaDesc}>{tarefa.descritivo}</div>
        <div style={metaLine}>
          {tarefa.responsavel && <span style={metaItem}>👤 {tarefa.responsavel}</span>}
          <span style={{ ...metaItem, color: STATUS_COR[tarefa.status] || "#94a3b8" }}>
            ● {STATUS_LABEL[tarefa.status] || tarefa.status}
          </span>
        </div>
      </div>

      {/* Coluna direita: ações */}
      <div style={acaoCol}>
        {acoes.map(a => (
          <button
            key={a.key}
            style={acaoBtn(a.cor)}
            onClick={() => onStatusChange(a.key)}
          >
            → {a.label}
          </button>
        ))}
        {!definindoPrazo && (
          <button
            style={acaoBtn(tarefa.data_entrega ? "#fbbf24" : "#60a5fa")}
            onClick={() => {
              setNovoPrazo(tarefa.data_entrega ? tarefa.data_entrega.slice(0, 10) : "");
              setDefinindoPrazo(true);
            }}
          >
            {tarefa.data_entrega ? "✎ Prazo" : "📅 Definir prazo"}
          </button>
        )}
        {definindoPrazo && (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input
              type="date"
              autoFocus
              style={prazoInput}
              value={novoPrazo}
              onChange={e => setNovoPrazo(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") salvarPrazo(); if (e.key === "Escape") setDefinindoPrazo(false); }}
            />
            <button style={acaoBtn("#4ade80")} onClick={salvarPrazo}>✓</button>
            <button style={acaoBtn("#f87171")} onClick={() => setDefinindoPrazo(false)}>✕</button>
          </div>
        )}
      </div>

    </div>
  );
}
/* ─── Estilos ─────────────────────────────────────────────────────── */

const page = {
  padding: "28px 32px",
  display: "flex",
  flexDirection: "column",
  gap: 24,
  maxWidth: 1000,
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
};

const pageTitle = {
  color: "#f1f5f9",
  fontSize: 22,
  fontWeight: 700,
  margin: 0,
};

const pageSub = {
  color: "#475569",
  fontSize: 13,
  marginTop: 4,
};

const refreshBtn = {
  padding: "8px 14px",
  borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.05)",
  color: "#60a5fa",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const kpiRow = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 14,
};

const kpiCard = (cor, ativo) => ({
  background: ativo ? `${cor}18` : "rgba(11,22,42,0.6)",
  border: `1px solid ${ativo ? cor + "55" : "rgba(96,165,250,0.08)"}`,
  borderRadius: 14,
  padding: "18px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  cursor: "pointer",
  transition: "all 0.15s",
  boxShadow: ativo ? `0 0 16px ${cor}22` : "none",
});

const grupoHeader = cor => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 0 10px",
  borderBottom: `2px solid ${cor}44`,
  color: cor,
  marginBottom: 4,
});

const grupoBadge = cor => ({
  marginLeft: "auto",
  background: `${cor}22`,
  color: cor,
  border: `1px solid ${cor}44`,
  borderRadius: 10,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 700,
});

const lista = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const row = {
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
  padding: "12px 14px",
  borderRadius: 10,
  background: "rgba(11,22,42,0.5)",
  border: "1px solid rgba(96,165,250,0.07)",
  marginBottom: 4,
};

const urgCol = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  minWidth: 90,
  flexShrink: 0,
};

const urgBadge = cor => ({
  fontSize: 11,
  fontWeight: 700,
  color: cor,
  background: `${cor}18`,
  border: `1px solid ${cor}44`,
  borderRadius: 6,
  padding: "3px 8px",
  textAlign: "center",
  whiteSpace: "nowrap",
});

const infoCol = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 5,
  minWidth: 0,
};

const contextLine = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
};

const projetoPaiChip = {
  fontSize: 10,
  fontWeight: 600,
  color: "#a78bfa",
  background: "rgba(167,139,250,0.08)",
  border: "1px solid rgba(167,139,250,0.18)",
  borderRadius: 4,
  padding: "1px 6px",
};

const projetoChip = {
  fontSize: 10,
  fontWeight: 600,
  color: "#22d3ee",
  background: "rgba(34,211,238,0.08)",
  border: "1px solid rgba(34,211,238,0.18)",
  borderRadius: 4,
  padding: "1px 6px",
};

const ticketChip = {
  fontSize: 10,
  fontWeight: 600,
  color: "#60a5fa",
  background: "rgba(96,165,250,0.08)",
  border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 4,
  padding: "1px 6px",
  wordBreak: "break-word",
};

const tarefaDesc = {
  color: "#e2e8f0",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.4,
};

const metaLine = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const metaItem = {
  fontSize: 11,
  color: "#94a3b8",
};

const acaoCol = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  alignItems: "flex-end",
  flexShrink: 0,
};

const acaoBtn = cor => ({
  padding: "4px 10px",
  borderRadius: 7,
  border: `1px solid ${cor}44`,
  background: `${cor}11`,
  color: cor,
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const prazoInput = {
  padding: "4px 8px",
  borderRadius: 7,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(11,22,42,0.7)",
  color: "#e2e8f0",
  fontSize: 12,
  colorScheme: "dark",
  outline: "none",
};

const emptyRow = {
  color: "#334155",
  fontSize: 12,
  padding: "12px 14px",
  fontStyle: "italic",
};

const allOkBox = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  padding: "60px 0",
};

const skeletonPage = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "50vh",
};

/* ─── Estilos TelegramConfig ──────────────────────────────────────── */

const configBtn = {
  padding: "8px 14px", borderRadius: 9,
  border: "1px solid rgba(34,211,238,0.25)", background: "rgba(34,211,238,0.06)",
  color: "#22d3ee", fontSize: 12, fontWeight: 600, cursor: "pointer",
};

const filtroRespSelect = {
  padding: "6px 10px", borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(11,22,42,0.7)",
  color: "#e2e8f0", fontSize: 12, outline: "none", cursor: "pointer", colorScheme: "dark",
};

